import { useEffect, useState, useMemo, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Spin,
  Divider,
  Typography,
  Popconfirm,
  Tooltip,
  Collapse,
  Badge,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SaveOutlined,
  EditOutlined,
  TagOutlined,
} from "@ant-design/icons";
import apiHelpers from "./data";
import { STORES, hashForLabel } from "../BlogsTable/stores";
import { triggerNotification } from "../../common/notify";
import TagSelector from "../../common/TagSelector";

const STORE_OPTIONS = STORES.map((s) => ({ value: s.label, label: `${s.label} — ${s.name}` }));

const emptyOption = (sort_order = 0) => ({
  option_key: "",
  title: "",
  sort_order,
});

const emptyForm = (defaultStoreLabel, step = 1) => ({
  store_label: defaultStoreLabel || "UAT",
  store_hash: hashForLabel(defaultStoreLabel || "UAT"),
  que_key: "",
  code: "",
  title: "",
  step,
  option_type: "single",
  options: [emptyOption(1)],
});

const normalizeIncoming = (data) => ({
  store_label: data.storeLabel || "",
  store_hash: data.storeHash || hashForLabel(data.storeLabel),
  que_key: data.queKey || "",
  code: data.queCode || "",
  title: data.title || "",
  step: Number(data.step) || 1,
  option_type: data.optionType || "single",
  options: (data.options || []).map((o) => ({
    id: o.id,                              // stable PK — sent back on update so backend matches by id
    option_key: o.optionKey || "",
    title: o.title || "",
    sort_order: Number(o.sortOrder) || 0,
    // keywordPoints comes from the updated GET response; used only to seed tag-count badges
    _keywordPoints: Array.isArray(o.keywordPoints) ? o.keywordPoints : [],
  })),
});

// ── Keyword Points Panel (TagSelector-based) ──────────────────────────────────

const tagsKey = (tags) => tags.map((t) => `${t.id}:${t.points}`).join(",");

const KeywordPointsPanel = forwardRef(({ questionId, optionId, readOnly, onCountChange }, ref) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [savedKey,     setSavedKey]     = useState("");
  const [loadingKp, setLoadingKp] = useState(false);
  const [saving, setSaving] = useState(false);

  const isDirty = tagsKey(selectedTags) !== savedKey;

  useEffect(() => {
    onCountChange?.(selectedTags.length);
  }, [selectedTags.length, onCountChange]);

  useImperativeHandle(ref, () => ({
    saveIfDirty: () => (isDirty ? handleSave() : Promise.resolve()),
  }));

  const loadKp = useCallback(async () => {
    if (!questionId || !optionId) return;
    setLoadingKp(true);
    try {
      const records = await apiHelpers.fetchOptionKeywordPoints(questionId, optionId);
      // Convert existing records → TagSelector value format
      // Each record has { id (kp row id), keyword, points }
      // We use keyword as the tag name; use record.id as a stable key.
      // TagSelector needs { id, tag, points } where id must be unique per chip.
      // We store kp record id in a separate field to allow saving updates.
      const tags = records.map((r) => ({
        id: r.id,
        tag: r.keyword,
        points: Number(r.points) || 0,
        _kpId: r.id,
      }));
      setSelectedTags(tags);
      setSavedKey(tagsKey(tags));
    } catch {
      // silently ignore
    } finally {
      setLoadingKp(false);
    }
  }, [questionId, optionId]);

  useEffect(() => { loadKp(); }, [loadKp]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const keywords = selectedTags.map((t) => ({
        keyword: t.tag,
        points: Number(t.points) || 0,
      }));
      const updated = await apiHelpers.bulkUpsertOptionKp(questionId, optionId, keywords);
      const normalized = Array.isArray(updated) ? updated : [];
      const savedTags = normalized.map((r) => ({
        id: r.id,
        tag: r.keyword,
        points: Number(r.points) || 0,
        _kpId: r.id,
      }));
      setSelectedTags(savedTags);
      setSavedKey(tagsKey(savedTags));
      triggerNotification({
        type: "success",
        title: `${keywords.length} tag${keywords.length === 1 ? "" : "s"} saved`,
      });
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      triggerNotification({ type: "error", title: "Save failed", message: mapped.message });
    } finally {
      setSaving(false);
    }
  };

  if (!optionId) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Save the question first to manage keyword points.
      </Typography.Text>
    );
  }

  if (loadingKp) {
    return (
      <div style={{ textAlign: "center", padding: 12 }}>
        <Spin size="small" />
      </div>
    );
  }

  return (
    <div>
      <TagSelector
        value={selectedTags}
        onChange={setSelectedTags}
        withPoints
        disabled={readOnly}
        placeholder="Search and select tags…"
      />
      {!readOnly && (
        <div style={{ marginTop: 10, textAlign: "right" }}>
          <Button
            type="primary"
            size="small"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
            disabled={!isDirty}
          >
            Save tags
          </Button>
        </div>
      )}
    </div>
  );
});

// ── Main Drawer ───────────────────────────────────────────────────────────────

const QuestionDrawer = ({
  open,
  mode,
  questionId,
  defaultStoreLabel,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [internalMode, setInternalMode] = useState(mode || "view");
  const [optionKeyTouched, setOptionKeyTouched] = useState({});
  const [queKeyTouched, setQueKeyTouched] = useState(false);
  const [queCodeTouched, setQueCodeTouched] = useState(false);
  const [optionTagCounts, setOptionTagCounts] = useState({});
  const [formDirty, setFormDirty] = useState(false);
  const kpPanelRefs = useRef({});
  const initialValuesRef = useRef(null);

  const checkDirty = useCallback(() => {
    const current = JSON.stringify(form.getFieldsValue(true));
    setFormDirty(current !== initialValuesRef.current);
  }, [form]);

  const isCreate = internalMode === "create";
  const isEdit = internalMode === "edit";
  const isView = internalMode === "view";
  const readOnly = isView;

  useEffect(() => {
    setInternalMode(mode || "view");
  }, [mode]);

  const applyNextStep = useCallback(async (storeLabel) => {
    try {
      const max = await apiHelpers.fetchMaxStep(storeLabel);
      form.setFields([{ name: "step", value: max + 1, errors: [] }]);
    } catch (err) {
      console.error("[applyNextStep] failed:", err);
    }
  }, [form]);

  useEffect(() => {
    if (!open) return;
    setQueKeyTouched(false);
    setQueCodeTouched(false);
    setOptionKeyTouched({});
    setFormDirty(false);
    if (mode === "create" || !questionId) {
      const storeLabel = defaultStoreLabel || "UAT";
      // Reset form first with step=1, then async-fetch the real next step
      form.resetFields();
      const initial = emptyForm(storeLabel, 1);
      form.setFieldsValue(initial);
      initialValuesRef.current = JSON.stringify(form.getFieldsValue(true));
      applyNextStep(storeLabel);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiHelpers.fetchQuestionById(questionId);
        if (cancelled) return;
        const normalized = normalizeIncoming(data);
        form.setFieldsValue(normalized);
        initialValuesRef.current = JSON.stringify(form.getFieldsValue(true));
        setFormDirty(false);
        // Seed tag-count badges from the keywordPoints already embedded in GET response
        const seedCounts = {};
        normalized.options.forEach((o) => {
          if (o.id && Array.isArray(o._keywordPoints) && o._keywordPoints.length > 0) {
            seedCounts[o.id] = o._keywordPoints.length;
          }
        });
        if (Object.keys(seedCounts).length > 0) {
          setOptionTagCounts((prev) => ({ ...prev, ...seedCounts }));
        }
      } catch (err) {
        const mapped = apiHelpers.mapError(err);
        triggerNotification({
          type: "error",
          title: "Failed to load question",
          message: mapped.message,
        });
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, questionId, internalMode, applyNextStep]);

  const handleStoreChange = (label) => {
    form.setFieldsValue({ store_hash: hashForLabel(label) });
    checkDirty();
    if (mode === "create") applyNextStep(label);
  };

  const handleTitleChange = (e) => {
    if (!queKeyTouched && (isCreate || isEdit)) {
      form.setFieldsValue({ que_key: apiHelpers.slugify(e.target.value) });
    }
    if (!queCodeTouched && (isCreate || isEdit)) {
      form.setFieldsValue({ code: e.target.value }); // plain text, not slugified
    }
    checkDirty();
  };

  const handleOptionTitleChange = (idx, val) => {
    if (!optionKeyTouched[idx]) {
      const options = form.getFieldValue("options") || [];
      const next = [...options];
      next[idx] = { ...next[idx], option_key: apiHelpers.slugify(val) };
      form.setFieldsValue({ options: next });
    }
    checkDirty();
  };

  const moveOption = (idx, dir) => {
    const options = form.getFieldValue("options") || [];
    const j = idx + dir;
    if (j < 0 || j >= options.length) return;
    const next = [...options];
    [next[idx], next[j]] = [next[j], next[idx]];
    next.forEach((o, i) => (o.sort_order = i + 1));
    form.setFieldsValue({ options: next });
    checkDirty();
  };

  const addOption = () => {
    const options = form.getFieldValue("options") || [];
    const next = [...options, emptyOption(options.length + 1)];
    form.setFieldsValue({ options: next });
    checkDirty();
  };

  const removeOption = (idx) => {
    const options = form.getFieldValue("options") || [];
    if (options.length <= 1) return;
    const next = options.filter((_, i) => i !== idx);
    next.forEach((o, i) => (o.sort_order = i + 1));
    form.setFieldsValue({ options: next });
    checkDirty();
    setOptionKeyTouched((p) => {
      const copy = { ...p };
      delete copy[idx];
      return copy;
    });
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!values.options || values.options.length === 0) {
      triggerNotification({
        type: "error",
        title: "Add at least one option",
        message: "A question requires at least one option.",
      });
      return;
    }

    const payload = {
      ...values,
      store_hash: values.store_hash || hashForLabel(values.store_label),
      step: Number(values.step) || 1,
      options: values.options.map((o, i) => {
        const opt = {
          title: o.title,
          sort_order: Number(o.sort_order) || i + 1,
        };
        // If the option already exists in DB, send its id so backend updates in-place (no new row)
        if (o.id) {
          opt.id = o.id;
        } else {
          // New option — needs option_key for creation
          opt.option_key = o.option_key || apiHelpers.slugify(o.title) || `option_${i + 1}`;
        }
        return opt;
      }),
    };

    setSaving(true);
    try {
      const saved = isCreate
        ? await apiHelpers.createQuestion(payload)
        : await apiHelpers.updateQuestion(questionId, payload);

      // Flush any unsaved tag/point edits sitting in the per-option Keyword Points panels
      const pendingKpSaves = Object.values(kpPanelRefs.current)
        .filter(Boolean)
        .map((panel) => panel.saveIfDirty());
      if (pendingKpSaves.length > 0) {
        await Promise.all(pendingKpSaves);
      }

      triggerNotification({
        type: "success",
        title: isCreate ? "Question created" : "Question updated",
        message: saved?.title || "Saved successfully.",
      });
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      // Highlight the step field if it's a duplicate step error
      if (mapped.code === "DUPLICATE_STEP") {
        form.setFields([{ name: "step", errors: [mapped.message] }]);
      }
      triggerNotification({
        type: "error",
        title: isCreate ? "Create failed" : "Update failed",
        message: mapped.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const title = useMemo(() => {
    if (isCreate) return "New Question";
    if (isEdit) return "Edit Question";
    return "Question Details";
  }, [isCreate, isEdit]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={720}
      title={title}
      destroyOnClose
      maskClosable={false}
      rootClassName={readOnly ? "QuestionDrawer-readonly" : undefined}
      extra={
        isView ? (
          <Button icon={<EditOutlined />} onClick={() => setInternalMode("edit")}>
            Edit
          </Button>
        ) : (
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSubmit}
              disabled={!isCreate && !formDirty}
            >
              Save
            </Button>
          </Space>
        )
      }
    >
      <style>{`
        .QuestionDrawer-readonly .ant-input[disabled],
        .QuestionDrawer-readonly .ant-input-number-input[disabled],
        .QuestionDrawer-readonly textarea.ant-input[disabled],
        .QuestionDrawer-readonly .ant-select-disabled .ant-select-selection-item,
        .QuestionDrawer-readonly .ant-input-number-disabled .ant-input-number-input {
          color: rgba(0, 0, 0, 0.55) !important;
          -webkit-text-fill-color: rgba(0, 0, 0, 0.55) !important;
          cursor: not-allowed !important;
        }
        .QuestionDrawer-readonly .ant-input[disabled],
        .QuestionDrawer-readonly .ant-input-number-disabled,
        .QuestionDrawer-readonly textarea.ant-input[disabled],
        .QuestionDrawer-readonly .ant-select-disabled .ant-select-selector {
          background-color: #f5f5f5 !important;
          border-color: #e5e5e5 !important;
        }
        .QuestionDrawer-readonly .ant-switch-disabled {
          opacity: 0.7 !important;
        }
        .QuestionDrawer-readonly .ant-form-item-label > label {
          color: rgba(0, 0, 0, 0.65) !important;
        }
      `}</style>
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          disabled={readOnly}
          onValuesChange={checkDirty}
        >
          <Space size="middle" style={{ width: "100%" }} wrap>
            <Form.Item
              name="store_label"
              label="Store"
              rules={[{ required: true, message: "Store is required" }]}
              style={{ minWidth: 200 }}
            >
              <Select options={STORE_OPTIONS} onChange={handleStoreChange} />
            </Form.Item>
            <Form.Item
              name="step"
              label="Step"
              rules={[{ required: true, message: "Step is required" }]}
              style={{ minWidth: 110 }}
            >
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="option_type"
              label="Option Type"
              rules={[{ required: true }]}
              style={{ minWidth: 160 }}
            >
              <Select
                options={[
                  { value: "single", label: "Single choice" },
                ]}
              />
            </Form.Item>
          </Space>

          <Form.Item name="store_hash" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="title"
            label="Question title"
            rules={[
              { required: true, message: "Title is required" },
            ]}
          >
            <Input onChange={handleTitleChange} placeholder="e.g. What is your skin type?" />
          </Form.Item>

          <Form.Item
            name="que_key"
            label={
              <Space>
                <span>Question key</span>
                <Tooltip title="Unique slug used by the API. Auto-generated from title; edit to override.">
                  <Typography.Text type="secondary">(unique per store)</Typography.Text>
                </Tooltip>
              </Space>
            }
            rules={[
              { required: true, message: "Key is required" },
            ]}
          >
            <Input
              onChange={() => setQueKeyTouched(true)}
              placeholder="e.g. skin_type"
            />
          </Form.Item>

          <Form.Item
            name="code"
            label={
              <Space>
                <span>Question Short Text</span>
                <Tooltip title="Unique short for show in storefront.">
                  <Typography.Text type="secondary">(unique per store)</Typography.Text>
                </Tooltip>
              </Space>
            }
            rules={[
              { required: true, message: "Question Code is required" },
            ]}
          >
            <Input
              onChange={() => setQueCodeTouched(true)}
              placeholder="e.g. Skin Type"
            />
          </Form.Item>


          <Divider orientation="left">Options</Divider>

          <Form.List name="options">
            {(fields, { add }) => (
              <>
                {fields.map((field, idx) => {
                  const optionId = form.getFieldValue(["options", idx, "id"]) || null;

                  return (
                    <div
                      key={field.key}
                      style={{
                        border: "1px solid #f0f0f0",
                        borderRadius: 6,
                        padding: 12,
                        marginBottom: 12,
                        background: "#fafafa",
                      }}
                    >
                      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }}>
                        <Typography.Text strong>Option {idx + 1}</Typography.Text>
                        {!readOnly && (
                          <Space>
                            <Tooltip title="Move up">
                              <Button
                                size="small"
                                icon={<ArrowUpOutlined />}
                                onClick={() => moveOption(idx, -1)}
                                disabled={idx === 0}
                              />
                            </Tooltip>
                            <Tooltip title="Move down">
                              <Button
                                size="small"
                                icon={<ArrowDownOutlined />}
                                onClick={() => moveOption(idx, 1)}
                                disabled={idx === fields.length - 1}
                              />
                            </Tooltip>
                            <Popconfirm
                              title="Remove this option?"
                              onConfirm={() => removeOption(idx)}
                              okText="Remove"
                              okButtonProps={{ danger: true }}
                              disabled={fields.length <= 1}
                            >
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                disabled={fields.length <= 1}
                              />
                            </Popconfirm>
                          </Space>
                        )}
                      </Space>

                      <Form.Item
                        {...field}
                        label="Title"
                        name={[field.name, "title"]}
                        rules={[
                          { required: true, message: "Required" },
                        ]}
                      >
                        <Input
                          placeholder="e.g. Oily"
                          onChange={(e) => handleOptionTitleChange(idx, e.target.value)}
                        />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, "option_key"]}
                        hidden
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        {...field}
                        name={[field.name, "sort_order"]}
                        hidden
                      >
                        <InputNumber />
                      </Form.Item>

                      {/* ── Keyword Points tags and points── */}
                      {!isCreate && (
                        <Collapse
                          size="small"
                          ghost
                          items={[
                            {
                              key: "kp",
                              label: (
                                <Space>
                                  <TagOutlined />
                                  <span style={{ fontSize: 13 }}>Tags and Points</span>
                                  {(optionTagCounts[optionId] ?? 0) > 0 && (
                                    <Badge
                                      count={optionTagCounts[optionId]}
                                      style={{ backgroundColor: "#1677ff" }}
                                    />
                                  )}
                                </Space>
                              ),
                              children: (
                                <div style={{ paddingTop: 4 }}>
                                  <KeywordPointsPanel
                                    ref={(el) => {
                                      if (optionId) kpPanelRefs.current[optionId] = el;
                                    }}
                                    questionId={questionId}
                                    optionId={optionId}
                                    readOnly={readOnly}
                                    onCountChange={(count) =>
                                      setOptionTagCounts((prev) =>
                                        prev[optionId] === count ? prev : { ...prev, [optionId]: count }
                                      )
                                    }
                                  />
                                </div>
                              ),
                            },
                          ]}
                        />
                      )}
                    </div>
                  );
                })}
                {!readOnly && (
                  <Button
                    block
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      add(emptyOption(fields.length + 1));
                    }}
                  >
                    Add option
                  </Button>
                )}
              </>
            )}
          </Form.List>
        </Form>
      )}
    </Drawer>
  );
};

export default QuestionDrawer;
